import { useMemo } from "react";
import { useAppSelector } from "../redux";
import type { SessionPermissionAction } from "../utils/permissions/flattenRolePermissions";

export function useSessionPermissionAction(
  permissionKey: string
): SessionPermissionAction | undefined {
  return useAppSelector((state) => state.auth.sessionPermissionMap[permissionKey]);
}

export function useSessionPermissionsLoading(): boolean {
  return useAppSelector((state) => state.auth.sessionPermissionsLoading);
}

export function useSessionPermissionsLoaded(): boolean {
  return useAppSelector((state) => state.auth.sessionPermissionsLoaded);
}

/** True when user may open the module (edit or view). Without role_id, legacy routing applies (always allow here). */
export function useSessionCanAccessModule(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector((state) => state.auth.sessionPermissionMap[permissionKey]);

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return action === "edit" || action === "view";
  }, [roleId, loading, loaded, action]);
}

export function useSessionModuleReadOnly(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const action = useAppSelector((state) => state.auth.sessionPermissionMap[permissionKey]);

  return !!roleId && loaded && action === "view";
}

/** After session load: role uses API but key is missing or NA. */
/** True when user may mutate (edit action). Without role_id, legacy allow. */
export function useSessionCanEditModule(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector((state) => state.auth.sessionPermissionMap[permissionKey]);

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return action === "edit";
  }, [roleId, loading, loaded, action]);
}

export function useSessionModuleDenied(permissionKey: string): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const action = useAppSelector((state) => state.auth.sessionPermissionMap[permissionKey]);

  return useMemo(() => {
    if (!roleId || loading || !loaded) return false;
    return action === undefined || action === "na";
  }, [roleId, loading, loaded, action]);
}

function allowsAccess(action: SessionPermissionAction | undefined): boolean {
  return action === "edit" || action === "view";
}

/** True if any key grants edit or view (OR). Without role_id, legacy allow. */
export function useSessionCanAccessAnyModule(permissionKeys: string[]): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPermissionMap = useAppSelector(
    (state) => state.auth.sessionPermissionMap
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return permissionKeys.some((key) =>
      allowsAccess(sessionPermissionMap[key])
    );
  }, [roleId, loading, loaded, sessionPermissionMap, permissionKeys]);
}

/** True if any key grants edit (OR). Without role_id, legacy allow. */
export function useSessionCanEditAnyModule(permissionKeys: string[]): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPermissionMap = useAppSelector(
    (state) => state.auth.sessionPermissionMap
  );

  return useMemo(() => {
    if (!roleId) return true;
    if (loading || !loaded) return true;
    return permissionKeys.some((key) => sessionPermissionMap[key] === "edit");
  }, [roleId, loading, loaded, sessionPermissionMap, permissionKeys]);
}

/** True when every key is missing or NA after session load. */
export function useSessionAllModulesDenied(permissionKeys: string[]): boolean {
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loaded = useAppSelector((state) => state.auth.sessionPermissionsLoaded);
  const loading = useAppSelector((state) => state.auth.sessionPermissionsLoading);
  const sessionPermissionMap = useAppSelector(
    (state) => state.auth.sessionPermissionMap
  );

  return useMemo(() => {
    if (!roleId || loading || !loaded) return false;
    return permissionKeys.every((key) => {
      const action = sessionPermissionMap[key];
      return action === undefined || action === "na";
    });
  }, [roleId, loading, loaded, sessionPermissionMap, permissionKeys]);
}
